/**
 * The tour sheet in one neutral shape, read from either surface.
 *
 * The office reads GET /well-servicing/tours/:id (camelCase); the phone reads
 * GET /well-servicing/field/tours/:id (snake_case, the API's snakeKeys). Each
 * is mapped here, field by field — never by a blanket re-casing, because the
 * value-keyed maps (hours by billing class, fuel litres by kind) carry keys
 * that are data. The editor, its rules and the body it sends are then one.
 *
 * Every hour figure here is the API's, computed from the times it stored. The
 * screens show them as sent and never compute one.
 */
import type { WsCatalogEntry, WsFieldTour, WsJobDetail, WsRigDetail, WsTour, WsTourGap } from '../../types/well-servicing';

export interface TourActivityValue {
  from: string;
  to: string;
  activityCodeId: string | null;
  code: string;
  codeLabel: string | null;
  billingClass: string | null;
  description: string | null;
  depth: { value: number; unit: string } | null;
  hours: number | null;
}

export interface TourCrewValue {
  userId: string;
  userName: string | null;
  position: string;
  crewShift: string;
  on: string | null;
  off: string | null;
  hours: number | null;
  travelHours: number | null;
  subsistence: boolean;
  notes: string | null;
}

export interface TourUnitValue {
  kind: 'owned' | 'rent_in';
  equipmentId: string | null;
  rentInId: string | null;
  name: string | null;
  hours: number | null;
  hourMeterEnd: number | null;
  notes: string | null;
}

export interface TourFuelValue {
  entryKind: string;
  fuelType: string;
  quantity: { value: number; unit: string } | null;
  litres: number | null;
  equipmentId: string | null;
  supplierRef: string | null;
  notes: string | null;
}

export interface TourValues {
  id: string;
  jobId: string;
  workDate: string;
  status: string;
  statusLabel: string | null;
  locked: boolean;
  returnedReason: string | null;
  startTime: string;
  endTime: string;
  depthSummary: string | null;
  remarks: string | null;
  activities: TourActivityValue[];
  crew: TourCrewValue[];
  equipment: TourUnitValue[];
  fuel: TourFuelValue[];
  /** The API's timeline of the SAVED sheet. */
  timeline: { tourHours: number | null; coveredHours: number | null; gaps: WsTourGap[]; complete: boolean };
  hoursByBillingClass: Record<string, number> | null;
  totals: { crewHours: number | null; crewTravelHours: number | null; ownedEquipmentHours: number | null; rentInHours: number | null; fuelLitres: Record<string, number> | null } | null;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const kindOf = (k: unknown): 'owned' | 'rent_in' => (k === 'rent_in' ? 'rent_in' : 'owned');

export function officeTourValues(t: WsTour): TourValues {
  return {
    id: t.id,
    jobId: t.jobId,
    workDate: t.workDate,
    status: t.status,
    statusLabel: t.statusLabel,
    locked: !!t.locked,
    returnedReason: t.returnedReason ?? null,
    startTime: t.startTime ?? '',
    endTime: t.endTime ?? '',
    depthSummary: t.depthSummary ?? null,
    remarks: t.remarks ?? null,
    activities: (t.activities ?? []).map((a) => ({
      from: a.from, to: a.to, activityCodeId: a.activityCodeId, code: a.code, codeLabel: a.codeLabel, billingClass: a.billingClass,
      description: a.description, depth: a.depth ?? null, hours: num(a.hours),
    })),
    crew: (t.crew ?? []).map((c) => ({
      userId: c.userId, userName: c.userName, position: c.position, crewShift: c.crewShift, on: c.on ?? null, off: c.off ?? null,
      hours: num(c.hours), travelHours: num(c.travelHours), subsistence: !!c.subsistence, notes: c.notes,
    })),
    equipment: (t.equipment ?? []).map((u) => ({
      kind: kindOf(u.kind), equipmentId: u.equipmentId, rentInId: u.rentInId,
      name: u.unitName ?? u.vendorName ?? null, hours: num(u.hours), hourMeterEnd: num(u.hourMeterEnd), notes: u.notes,
    })),
    fuel: (t.fuel ?? []).map((f) => ({
      entryKind: f.entryKind, fuelType: f.fuelType, quantity: f.quantity ?? null, litres: num(f.litres),
      equipmentId: f.equipmentId, supplierRef: f.supplierRef, notes: f.notes,
    })),
    timeline: {
      tourHours: num(t.timeline?.tourHours), coveredHours: num(t.timeline?.coveredHours),
      gaps: t.timeline?.gaps ?? [], complete: !!t.timeline?.complete,
    },
    hoursByBillingClass: t.hoursByBillingClass ?? null,
    totals: t.totals
      ? {
        crewHours: num(t.totals.crewHours), crewTravelHours: num(t.totals.crewTravelHours),
        ownedEquipmentHours: num(t.totals.ownedEquipmentHours), rentInHours: num(t.totals.rentInHours),
        fuelLitres: t.totals.fuelLitres ?? null,
      }
      : null,
  };
}

/** The phone's sheet. Totals and the billing-class split are office figures and are not read here. */
export function fieldTourValues(t: WsFieldTour): TourValues {
  return {
    id: t.id,
    jobId: t.job_id,
    workDate: t.work_date,
    status: t.status,
    statusLabel: t.status_label,
    locked: !!t.locked,
    returnedReason: t.returned_reason ?? null,
    startTime: t.start_time ?? '',
    endTime: t.end_time ?? '',
    depthSummary: t.depth_summary ?? null,
    remarks: t.remarks ?? null,
    activities: (t.activities ?? []).map((a) => ({
      from: a.from, to: a.to, activityCodeId: a.activity_code_id, code: a.code, codeLabel: a.code_label, billingClass: a.billing_class,
      description: a.description, depth: a.depth ?? null, hours: num(a.hours),
    })),
    crew: (t.crew ?? []).map((c) => ({
      userId: c.user_id, userName: c.user_name, position: c.position, crewShift: c.crew_shift, on: c.on ?? null, off: c.off ?? null,
      hours: num(c.hours), travelHours: num(c.travel_hours), subsistence: !!c.subsistence, notes: c.notes,
    })),
    equipment: (t.equipment ?? []).map((u) => ({
      kind: kindOf(u.kind), equipmentId: u.equipment_id, rentInId: u.rent_in_id,
      name: u.unit_name ?? u.vendor_name ?? null, hours: num(u.hours), hourMeterEnd: num(u.hour_meter_end), notes: u.notes,
    })),
    fuel: (t.fuel ?? []).map((f) => ({
      entryKind: f.entry_kind, fuelType: f.fuel_type, quantity: f.quantity ?? null, litres: num(f.litres),
      equipmentId: f.equipment_id, supplierRef: f.supplier_ref, notes: f.notes,
    })),
    timeline: {
      tourHours: num(t.timeline?.tour_hours), coveredHours: num(t.timeline?.covered_hours),
      gaps: t.timeline?.gaps ?? [], complete: !!t.timeline?.complete,
    },
    hoursByBillingClass: null,
    totals: null,
  };
}

// ─── Options ─────────────────────────────────────────────────────────────────

export interface TourCrewOption {
  userId: string;
  name: string;
  position: string | null;
}

export interface TourUnitOption {
  /** `owned:<equipmentId>` or `rent_in:<rentInId>`. */
  key: string;
  kind: 'owned' | 'rent_in';
  equipmentId: string | null;
  rentInId: string | null;
  name: string;
  detail: string | null;
}

export const unitKey = (kind: string, equipmentId: string | null, rentInId: string | null) =>
  kind === 'rent_in' ? `rent_in:${rentInId}` : `owned:${equipmentId}`;

const inRange = (day: string, from: string | null, to: string | null) => (!from || from <= day) && (!to || to >= day);

/**
 * The units a sheet may carry hours for on `day` — the API's rule, so a hand is
 * not offered a unit it would refuse: the job's rig carrier, its BOP stack,
 * owned units on the job that day, and rent-ins on rent that day. Units the
 * sheet already holds are always kept.
 */
export function tourUnitOptions(job: WsJobDetail | null, day: string, held: TourUnitValue[]): TourUnitOption[] {
  const out = new Map<string, TourUnitOption>();
  const put = (o: TourUnitOption) => { if (!out.has(o.key)) out.set(o.key, o); };
  if (job?.rigEquipmentId)
    put({ key: unitKey('owned', job.rigEquipmentId, null), kind: 'owned', equipmentId: job.rigEquipmentId, rentInId: null, name: job.rigNumber ?? '—', detail: 'Rig carrier' });
  if (job?.bopStackEquipmentId)
    put({ key: unitKey('owned', job.bopStackEquipmentId, null), kind: 'owned', equipmentId: job.bopStackEquipmentId, rentInId: null, name: job.bopStackName ?? 'BOP stack', detail: 'BOP stack' });
  for (const e of job?.equipment ?? [])
    if (inRange(day, e.onDate, e.offDate))
      put({ key: unitKey('owned', e.equipmentId, null), kind: 'owned', equipmentId: e.equipmentId, rentInId: null, name: e.equipmentName ?? '—', detail: e.unitNumber ?? e.serialNumber ?? null });
  for (const r of job?.rentIns ?? [])
    if (inRange(day, r.onRentOn, r.offRentOn))
      put({ key: unitKey('rent_in', null, r.id), kind: 'rent_in', equipmentId: null, rentInId: r.id, name: r.description || '—', detail: `Rented from ${r.vendorName || '—'}` });
  for (const h of held)
    put({ key: unitKey(h.kind, h.equipmentId, h.rentInId), kind: h.kind, equipmentId: h.equipmentId, rentInId: h.rentInId, name: h.name ?? '—', detail: null });
  return Array.from(out.values());
}

/** The rig's crew members, then anyone else offered (the office's people list), then whoever the sheet holds. */
export function tourCrewOptions(rig: WsRigDetail | null, held: TourCrewValue[], others: { id: string; name: string }[] = []): TourCrewOption[] {
  const out = new Map<string, TourCrewOption>();
  for (const c of rig?.crews ?? [])
    for (const m of c.members ?? [])
      if (!out.has(m.userId)) out.set(m.userId, { userId: m.userId, name: m.userName ?? '—', position: m.position ?? null });
  for (const u of others) if (!out.has(u.id)) out.set(u.id, { userId: u.id, name: u.name, position: null });
  for (const h of held) if (!out.has(h.userId)) out.set(h.userId, { userId: h.userId, name: h.userName ?? '—', position: h.position });
  return Array.from(out.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** The catalog lists the editor needs; every one may be missing on a pre-WS-2 payload. */
export interface TourCatalog {
  crewPositions: WsCatalogEntry[];
  crewShifts: WsCatalogEntry[];
  fuelEntryKinds: WsCatalogEntry[];
  fuelTypes: WsCatalogEntry[];
  fuelUnits: string[];
  depthUnits: string[];
}
